import React, { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import { MapPin, PlusCircle } from "lucide-react";

import { Button } from "~/components/ui/button";
import { EmptyState } from "~/components/ui/empty-state";
import { Skeleton } from "~/components/ui/skeleton";
import { CHILEAN_COMUNAS, CHILEAN_REGIONS } from "~/constants/places";
import { useToast } from "~/hooks/use-toast";
import { useClientAddresses } from "~/hooks/useClientAddresses";
import TabSectionHeader from "~/pages/clients/components/client-detail/TabSectionHeader";
import AddressCard from "~/pages/clients/components/client-detail/address/AddressCard";
import AddressDialog from "~/pages/clients/components/client-detail/address/AddressDialog";
import { AddressFormValues } from "~/pages/clients/components/client-detail/address/AddressForm";
import {
  fetchClientFiscalAddress,
  setClientFiscalAddress,
} from "~/services/addressService";
import { Address } from "~/types/client";

const AddressesTab: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState<Address | null>(null);
  const [fiscalAddress, setFiscalAddress] = useState<Address | null>(null);
  const { toast } = useToast();

  const {
    addresses,
    isLoading,
    addAddress,
    updateAddress,
    deleteAddress,
    selectedRegion,
    setSelectedRegion,
    selectedComuna,
    setSelectedComuna,
    availableComunas,
    editSelectedRegion,
    setEditSelectedRegion,
    editSelectedComuna,
    setEditSelectedComuna,
    editAvailableComunas,
  } = useClientAddresses(clientId);

  // Fetch fiscal address on component mount
  useEffect(() => {
    const fetchFiscalAddress = async () => {
      if (clientId) {
        const address = await fetchClientFiscalAddress(clientId);
        setFiscalAddress(address);
      }
    };

    fetchFiscalAddress();
  }, [clientId]);

  const handleAddSubmit = async (values: AddressFormValues) => {
    const success = await addAddress(values);
    if (success) {
      setIsAddDialogOpen(false);
    }
  };

  const handleEditSubmit = async (values: AddressFormValues) => {
    if (!currentAddress) return;

    const success = await updateAddress(currentAddress.id, values);
    if (success) {
      setIsEditDialogOpen(false);
    }
  };

  const handleEdit = (address: Address) => {
    setCurrentAddress(address);

    // Find and set the region and comuna based on the address using constants data
    if (address.state) {
      // Find region by name using constants data
      const regionWithName = CHILEAN_REGIONS.find(
        (r) => r.label === address.state
      );

      if (regionWithName) {
        setEditSelectedRegion(regionWithName.value);

        if (address.comuna) {
          // Set comuna with a small delay to ensure region is processed first
          setTimeout(() => {
            const comunaWithName = CHILEAN_COMUNAS.find(
              (c) =>
                c.label === address.comuna && c.region === regionWithName.value
            );
            if (comunaWithName) {
              setEditSelectedComuna(comunaWithName.value);
            }
          }, 10);
        } else {
          setEditSelectedComuna("");
        }
      } else {
        setEditSelectedRegion("");
        setEditSelectedComuna("");
      }
    } else {
      setEditSelectedRegion("");
      setEditSelectedComuna("");
    }

    setIsEditDialogOpen(true);
  };

  const handleSetAsFiscalAddress = async (address: Address) => {
    if (!clientId) return;

    try {
      const success = await setClientFiscalAddress(clientId, address.id);
      if (success) {
        setFiscalAddress(address);
        toast({
          title: "Dirección fiscal actualizada",
          description: "La dirección fiscal ha sido establecida correctamente",
        });
      }
    } catch (error) {
      console.error("Error setting fiscal address:", error);
      toast({
        title: "Error",
        description: "No se pudo establecer la dirección fiscal",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 pt-6 md:p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-10 w-44" />
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Skeleton className="h-40 rounded-lg" />
          <Skeleton className="h-40 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 pt-6 md:p-6">
      <TabSectionHeader
        title="Direcciones"
        description={
          addresses.length > 0
            ? `${addresses.length} ${addresses.length === 1 ? "dirección registrada" : "direcciones registradas"}`
            : undefined
        }
        action={
          <Button
            className="flex items-center gap-2"
            onClick={() => setIsAddDialogOpen(true)}
          >
            <PlusCircle className="h-4 w-4" />
            Agregar dirección
          </Button>
        }
      />

      {addresses.length === 0 ? (
        <EmptyState
          icon={MapPin}
          title="Este cliente no tiene direcciones registradas"
          description="Agregue una dirección para utilizarla en las entregas."
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {addresses.map((address) => (
            <AddressCard
              key={address.id}
              address={address}
              onEdit={handleEdit}
              onDelete={deleteAddress}
              isFiscalAddress={fiscalAddress?.id === address.id}
              onSetAsFiscal={handleSetAsFiscalAddress}
            />
          ))}
        </div>
      )}

      {/* Add Address Dialog */}
      <AddressDialog
        isOpen={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        title="Agregar nueva dirección"
        description="Ingrese los detalles de la nueva dirección de entrega."
        onSubmit={handleAddSubmit}
        selectedRegion={selectedRegion}
        setSelectedRegion={setSelectedRegion}
        selectedComuna={selectedComuna}
        setSelectedComuna={setSelectedComuna}
        availableComunas={availableComunas}
      />

      {/* Edit Address Dialog */}
      {currentAddress && (
        <AddressDialog
          isOpen={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          title="Editar dirección"
          description="Actualice los detalles de la dirección de entrega."
          onSubmit={handleEditSubmit}
          defaultValues={{
            street: currentAddress.street,
            number: currentAddress.number,
            complement: currentAddress.complement,
            city: currentAddress.city,
            state: currentAddress.state,
            comuna: currentAddress.comuna,
            isDefault: currentAddress.isDefault,
          }}
          selectedRegion={editSelectedRegion}
          setSelectedRegion={setEditSelectedRegion}
          selectedComuna={editSelectedComuna}
          setSelectedComuna={setEditSelectedComuna}
          availableComunas={editAvailableComunas}
          isEdit={true}
        />
      )}
    </div>
  );
};

export default AddressesTab;
